import { useContext, useEffect } from 'react';
import {
  generatePath,
  useHistory,
  useParams,
  useRouteMatch
} from 'react-router-dom';
import { Breadcrumb, Breadcrumbs } from 'components/breadcrumbs';
import {
  ROUTE_USER_ENTITY_PROGRAMS,
  ROUTE_USER_ENTITY_PROGRAMS_PROGRAM,
  ROUTE_USER_ENTITY_PROGRAMS_PROGRAM_FEEDBACK
} from 'routes/RouteList';
import { FormattedMessage } from 'react-intl';
import messages from 'messages';
import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  PanelTitleButton,
  PanelTitleWrapper
} from 'components/panel';
import Loader from 'components/loader/Loader';
import { Tabs } from 'components/tabs';
import { Tab, TabList, TabPanel } from 'react-tabs';
import { StoreContext } from 'index';
import { getLocalDateFormat } from 'utils/date';
import ExerciseFeedbackPanel from 'containers/pages/person/programs/ExerciseFeedbackPanel';
import useTabIndex from 'hooks/utils/useTabIndex';
import MenuWrapper from 'components/menu/MenuWrapper';
import DownloadExerciseProgramButton from 'containers/partials/buttons/DownloadExerciseProgramButton';
import TabPanelBody from 'components/tabs/TabPanelBody';
import useProgram from 'hooks/queries/useProgram';
import { useStore } from 'stores/Store';
import ProgramExercisesTab from 'containers/pages/exercises/programs/ProgramExercisesTab';
import RetryPanel from 'containers/partials/error-boundary/RetryPanel';

const Program = () => {
  const {
    authStore: { user }
  } = useContext(StoreContext);
  const { entityId, programId } = useParams();
  const { push } = useHistory();
  const [tabIndex, setTabIndex] = useTabIndex([
    ROUTE_USER_ENTITY_PROGRAMS_PROGRAM,
    ROUTE_USER_ENTITY_PROGRAMS_PROGRAM_FEEDBACK
  ]);
  const active = useRouteMatch([
    ROUTE_USER_ENTITY_PROGRAMS_PROGRAM_FEEDBACK,
    ROUTE_USER_ENTITY_PROGRAMS_PROGRAM
  ]);

  const { fetchProgram, error, loading } = useProgram();
  const program = useStore(state => state.program);

  useEffect(() => {
    if (programId) {
      fetchProgram({ variables: { id: programId } });
    }
  }, [programId]);

  const exercisesTab = () => {
    push(
      generatePath(ROUTE_USER_ENTITY_PROGRAMS_PROGRAM, {
        entityId,
        programId
      })
    );
  };

  const feedbackTab = () => {
    push(
      generatePath(ROUTE_USER_ENTITY_PROGRAMS_PROGRAM_FEEDBACK, {
        entityId,
        programId
      })
    );
  };

  if (!program)
    return (
      <Panel active={active?.isExact}>
        <Loader />
      </Panel>
    );
  if (error) return <RetryPanel />;

  return (
    <Panel active={active?.isExact}>
      <PanelHeader>
        <Breadcrumbs>
          <Breadcrumb
            to={generatePath(ROUTE_USER_ENTITY_PROGRAMS, {
              entityId,
              programId
            })}
          >
            <FormattedMessage {...messages.breadcrumbPrograms} />
          </Breadcrumb>
        </Breadcrumbs>
        <PanelTitleWrapper>
          <PanelTitle>
            {program.title}
            <MenuWrapper trigger={<PanelTitleButton />}>
              <DownloadExerciseProgramButton programId={program.id} />
            </MenuWrapper>
          </PanelTitle>
          <PanelSubtitle>
            <FormattedMessage
              {...messages.exercisesProgramSubTitle}
              values={{
                startdate: getLocalDateFormat(program.startdate),
                duration: program.duration
              }}
            />
          </PanelSubtitle>
        </PanelTitleWrapper>
      </PanelHeader>
      <PanelBody>
        {loading && <Loader />}

        <Tabs selectedIndex={tabIndex} onSelect={index => setTabIndex(index)}>
          <TabList>
            <Tab onClick={exercisesTab}>
              <FormattedMessage {...messages.tabExercises} />
            </Tab>
            <Tab onClick={feedbackTab}>
              <FormattedMessage {...messages.tabFeedback} />
            </Tab>
          </TabList>
          <TabPanel>
            <TabPanelBody>
              <ProgramExercisesTab program={program} />
            </TabPanelBody>
          </TabPanel>
          <TabPanel>
            {program?.id && (
              <TabPanelBody>
                <ExerciseFeedbackPanel
                  editable
                  program={program}
                  personId={user.id}
                />
              </TabPanelBody>
            )}
          </TabPanel>
        </Tabs>
      </PanelBody>
    </Panel>
  );
};

export default Program;
